import React, { useEffect, useRef, useState } from "react";
import { REIButton, REIDialoge, REIInputNumber } from "../../../../components/ui";
import { IAnalysedProperty, IPropertyOtherPurchaseExpense } from "../../../../interface/addAnalyser";
import { useAnalysedPropertyContext } from "./AnalysedPropertyContext";
import { Slider } from "primereact/slider";
import StampDutyCalculator from "./StampDutyCalculator";
import { isPropertyDetailValid } from "../../../../utils/commonUtil";

interface OtherPurchaseExpenseProps {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
}

export const OtherPurchaseExpense: React.FC<OtherPurchaseExpenseProps> = ({ setIsValid, updateProperty }) => {
  const { analysedProperty, userProfile } = useAnalysedPropertyContext();
  const [propertyPurchaseExpense, setPropertyPurchaseExpense] = useState<IPropertyOtherPurchaseExpense>();
  const [showCalculateStampDuty, setShowCalculateStampDuty] = useState(false);
  const [reset, setReset] = useState<boolean>(false);
  const hasInitializedUseDefault = useRef(false);
  const previousUseDefault = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (analysedProperty && !reset) {
      setPropertyPurchaseExpense(analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense);
      setReset(true);
    }
  }, [analysedProperty]);

  useEffect(() => {
    const currentUseDefault = analysedProperty?.PropertyDetail.useDefault;

    if (!hasInitializedUseDefault.current) {
      if (currentUseDefault === undefined) {
        return;
      }

      previousUseDefault.current = currentUseDefault;
      hasInitializedUseDefault.current = true;
      return;
    }

    const didEnableDefaults = previousUseDefault.current !== "1" && currentUseDefault === "1";
    previousUseDefault.current = currentUseDefault;

    if (
      analysedProperty?.PropertyDetail.PropertyDetailId !== 0 ||
      currentUseDefault !== "1" ||
      !userProfile ||
      !didEnableDefaults
    ) {
      return;
    }

    setPropertyPurchaseExpense((prevState) => {
      const existingExpense = prevState ?? analysedProperty?.LoanTypeCost.PropertyOtherPurchaseExpense;

      return {
        CapitalInvestment: userProfile.LoanCost.ImprovementFee,
        StampDuty: existingExpense?.StampDuty ?? 0,
        SolicitorsCost: userProfile.LoanCost.SolicitorFee,
        OtherCost: existingExpense?.OtherCost ?? 0,
      };
    });
  }, [
    analysedProperty?.LoanTypeCost.PropertyOtherPurchaseExpense,
    analysedProperty?.PropertyDetail.PropertyDetailId,
    analysedProperty?.PropertyDetail.useDefault,
    userProfile,
  ]);

  const setValues = (name: string, value: string | number) => {
    setPropertyPurchaseExpense((prevState) => {
      if (!prevState) return undefined;
      return {
        ...prevState,
        [name]: value !== undefined ? value : 0,
      };
    });
  };

  useEffect(() => {
    if (propertyPurchaseExpense) {
      setIsValid(propertyPurchaseExpense.CapitalInvestment > 0);
      updateProperty({
        LoanTypeCost: {
          ...analysedProperty?.LoanTypeCost,
          PropertyPurchaseCost: analysedProperty?.LoanTypeCost.PropertyPurchaseCost || {
            CashDeposit: 0,
            MarketValueNow: 0,
            PurchaseAmount: 0,
            EquityDeposit: 0,
            isEnterPercentage: "0",
            isEnterValue: "0",
          },
          Loans: analysedProperty?.LoanTypeCost.Loans || [],
          PropertyOtherPurchaseExpense: propertyPurchaseExpense,
        },
      });
    }
  }, [propertyPurchaseExpense]);

  const hideStampDutyDialog = (stampDutyValue?: number) => {
    setShowCalculateStampDuty(false);
    if (stampDutyValue !== undefined) {
      setValues("StampDuty", stampDutyValue);
    }
  };

  return (
    <>
      <REIDialoge
        showHeader={true}
        header={`Stamp duty calculator`}
        visible={showCalculateStampDuty}
        handleEditClick={() => hideStampDutyDialog()}
        position="top"
        customClass="update-statistics-dialog"
        style={{ width: "40vw" }}
        contentComponent={<StampDutyCalculator closeDialogue={hideStampDutyDialog} updateProperty={updateProperty} />}
      ></REIDialoge>

      <div className="w-full pt-2">
        <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
          <div className="default-profile-header-container">
            <h3 className="text-[var(--primary-color)] mt-0 text-lg">Other purchase expenses</h3>
          </div>
          {propertyPurchaseExpense && (
            <div className="mt-4">
              <div className="flex flex-col lg:flex-row lg:w-full gap-2">
                <div className="flex-0 flex-col w-full lg:w-1/2 gap-2">
                  <div className="flex w-full p-1">
                    <div className="w-[20%] mt-2">
                      Capital improvements at purchase ($) {isPropertyDetailValid(analysedProperty) ? "" : "*"}
                    </div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="ope-sldr-capitalimprovements"
                        data-testid="ope-sldr-capitalimprovements"
                        value={
                          propertyPurchaseExpense.CapitalInvestment > 1500000
                            ? 1500000
                            : propertyPurchaseExpense.CapitalInvestment
                        }
                        onChange={(e) => setValues("CapitalInvestment", Array.isArray(e.value) ? e.value[0] : e.value)}
                        className="w-full mt-4"
                        min={0}
                        max={1500000}
                        step={1000}
                      />
                    </div>
                    <div className="w-[40%] flex px-2">
                      <REIInputNumber
                        id="ope-num-capitalimprovements"
                        value={propertyPurchaseExpense.CapitalInvestment}
                        onValueChange={(e) => setValues("CapitalInvestment", e.target.value)}
                        className="w-full"
                        useGrouping={true}
                        min={0}
                      />
                    </div>
                  </div>
                </div>
                <div
                  hidden={analysedProperty?.PropertyDetail.CountryId === 2}
                  className="flex-0 flex-col w-full lg:w-1/2 gap-2"
                >
                  <div className="flex w-full p-1">
                    <div className="w-[22%] mt-2">
                      Stamp duty($) (
                      <REIButton
                        id="ope-btn-calculatestampduty"
                        label="Calculate"
                        classNames="p-button-link p-0"
                        onClick={() => {
                          setShowCalculateStampDuty(true);
                        }}
                      />
                      )
                    </div>
                    <div className="w-[38%] pr-2">
                      <Slider
                        id="ope-sldr-stampduty"
                        data-testid="ope-sldr-stampduty"
                        value={propertyPurchaseExpense.StampDuty > 100000 ? 100000 : propertyPurchaseExpense.StampDuty}
                        onChange={(e) => setValues("StampDuty", Array.isArray(e.value) ? e.value[0] : e.value)}
                        className="w-full mt-4"
                        min={0}
                        max={100000}
                        step={1000}
                      />
                    </div>
                    <div className="w-[40%] flex px-2">
                      <REIInputNumber
                        id="ope-num-stampduty"
                        value={propertyPurchaseExpense.StampDuty}
                        onValueChange={(e) => setValues("StampDuty", e.target.value)}
                        className="w-full"
                        useGrouping={true}
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row lg:w-full gap-2">
                <div className="flex-0 flex-col w-full lg:w-1/2 gap-2">
                  <div className="flex w-full p-1">
                    <div className="w-[20%] mt-2">
                      Solicitor’s cost ($) {isPropertyDetailValid(analysedProperty) ? "" : "*"}
                    </div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="ope-sldr-solicitorcost"
                        data-testid="ope-sldr-solicitorcost"
                        value={
                          propertyPurchaseExpense.SolicitorsCost > 50000
                            ? 50000
                            : propertyPurchaseExpense.SolicitorsCost
                        }
                        onChange={(e) => setValues("SolicitorsCost", Array.isArray(e.value) ? e.value[0] : e.value)}
                        className="w-full mt-4"
                        min={0}
                        max={50000}
                        step={1000}
                      />
                    </div>
                    <div className="w-[40%] flex px-2">
                      <REIInputNumber
                        id="ope-num-solicitorcost"
                        value={propertyPurchaseExpense?.SolicitorsCost}
                        onValueChange={(e) => setValues("SolicitorsCost", e.target.value)}
                        className="w-full"
                        useGrouping={true}
                        min={0}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-0 flex-col w-full lg:w-1/2 gap-2">
                  <div className="flex w-full p-1">
                    <div className="w-[20%] mt-2">
                      Other costs ($) {isPropertyDetailValid(analysedProperty) ? "" : "*"}
                    </div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="ope-sldr-othercost"
                        data-testid="ope-sldr-othercost"
                        value={propertyPurchaseExpense.OtherCost > 50000 ? 50000 : propertyPurchaseExpense.OtherCost}
                        onChange={(e) => setValues("OtherCost", Array.isArray(e.value) ? e.value[0] : e.value)}
                        className="w-full mt-4"
                        min={0}
                        max={50000}
                        step={1000}
                      />
                    </div>
                    <div className="w-[40%] flex px-2">
                      <REIInputNumber
                        id="ope-num-othercost"
                        value={propertyPurchaseExpense?.OtherCost}
                        onValueChange={(e) => setValues("OtherCost", e.target.value)}
                        className="w-full"
                        useGrouping={true}
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OtherPurchaseExpense;
